import { stripHtmlForSchema } from "./string.js";

const WORDS_PER_MINUTE = 200;

const COUNTABLE_FIELDS_BY_COMPONENT: Record<string, string[]> = {
    "Media and Text": ["heading", "description1", "description2"],
    "Editorial Text": ["content"],
    Text: ["text"],
};

type JsonRecord = Record<string, unknown>;

function isRecord(value: unknown): value is JsonRecord {
    return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isHiddenOrPixelBlock(block: JsonRecord): boolean {
    const id = typeof block.id === "string" ? block.id : "";
    const component = isRecord(block.component) ? block.component : {};
    const options = isRecord(component.options) ? component.options : {};
    const properties = isRecord(block.properties) ? block.properties : {};
    const className =
        typeof properties.class === "string"
            ? properties.class
            : typeof properties.className === "string"
              ? properties.className
              : "";
    const src = typeof properties.src === "string" ? properties.src : "";

    if (options.hideBlock === true || id.startsWith("builder-pixel-")) {
        return true;
    }

    if (className.includes("builder-pixel-")) {
        return true;
    }

    try {
        const url = new URL(src);
        return url.hostname === "cdn.builder.io" && url.pathname === "/api/v1/pixel";
    } catch {
        return false;
    }
}

function stripNonReadableMarkup(value: string): string {
    return value
        .replace(/<script[\s\S]*?<\/script>/gi, " ")
        .replace(/<style[\s\S]*?<\/style>/gi, " ");
}

function countWords(value: string): number {
    const text = stripHtmlForSchema(stripNonReadableMarkup(value));

    if (!text) {
        return 0;
    }

    return text.split(/\s+/).filter(Boolean).length;
}

function collectWordCount(value: unknown): number {
    if (Array.isArray(value)) {
        return value.reduce((total, item) => total + collectWordCount(item), 0);
    }

    if (!isRecord(value) || isHiddenOrPixelBlock(value)) {
        return 0;
    }

    let total = 0;
    const component = isRecord(value.component) ? value.component : {};
    const componentName = typeof component.name === "string" ? component.name : "";
    const options = isRecord(component.options) ? component.options : {};
    const countableFields = COUNTABLE_FIELDS_BY_COMPONENT[componentName] ?? [];

    for (const field of countableFields) {
        const fieldValue = options[field];

        if (typeof fieldValue === "string") {
            total += countWords(fieldValue);
        }
    }

    for (const nestedValue of Object.values(value)) {
        if (Array.isArray(nestedValue) || isRecord(nestedValue)) {
            total += collectWordCount(nestedValue);
        }
    }

    return total;
}

export function getReadingTimeMinutes(blocks: unknown): number | null {
    const wordCount = collectWordCount(blocks);

    if (wordCount === 0) {
        return null;
    }

    return Math.max(1, Math.ceil(wordCount / WORDS_PER_MINUTE));
}

export function formatReadingTime(minutes: number | null | undefined): string {
    if (!Number.isInteger(minutes) || !minutes || minutes < 1) {
        return "";
    }

    return `${minutes} min${minutes === 1 ? "" : "s"} read`;
}
